/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState, useMemo} from "react"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {Icon} from "components/Icon"
import {attendanceService} from "services"
import {getFullName, handleError, roundTwoDigits} from "helpers"
import styles from "../../common.module.css"
import rollCallStyles from "./RollCall.module.css"
import {cloneDeep, get, isEmpty, uniqBy} from "lodash"
import {useCurrentProfilePermissions, useModel, useMutation, useVisible} from "hooks"
import {AttendanceEvent, AttendanceEventType} from "types/attendance"
import moment from "moment"
import {useDebounce} from "react-use"
import {useFilterStorage} from "hooks/useFilterStorage"
import {FilterKey} from "types/filter"
import {exportCsv, exportExcel} from "helpers/export-table"
import {ExportFileType} from "components/ui"
import AddManualDailyAttendance from "../AddManualDailyAttendance/AddManualDailyAttendance"
import {useQuery} from "@tanstack/react-query"
import {
    DailyAttendanceFilter,
    DailyAttendanceFilterCheckin,
    DailyAttendanceFilterCheckout
} from "../DailyAttendanceFilter/DailyAttendanceFilter"
import {Checkbox, Collapse} from "antd"
import {YesNo} from "types/common"
import {BatchDailyAttendanceUpdates} from "../BatchDailyAttendanceUpdates"
import {Permissions} from "@edular/permissions"
const {Panel} = Collapse

export enum FilterEventType {
    Active = "active",
    InSchool = "inSchool",
    Break = "break",
    Checkout = "checkOut",
    Missed = "missed"
}

const initialDefaultFilter = {
    programVersions: [],
    filterEventType: undefined,
    campuses: [],
    instructors: [],
    startingTerms: [],
    studentStatuses: [],
    programs: [],
    groups: [],
    phases: [],
    shifts: [],
    filterCheckin: DailyAttendanceFilterCheckin.None,
    filterCheckout: DailyAttendanceFilterCheckout.None
}

function RollCallTable(props) {
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        getCurrentData,
        allFields,
        dispatch,
        defaultStudentStatuses = [],
        filter,
        isLoadedTableFuncs,
        date,
        setDate
    } = props

    const model = useModel()
    const userCampuses = model.getCurrentUserProfile()?.campuses ?? []

    const defaultFilter = useMemo(() => {
        return {...initialDefaultFilter, studentStatuses: defaultStudentStatuses, campuses: userCampuses}
    }, [defaultStudentStatuses, userCampuses])
    const filterKey = FilterKey.ListRollCall

    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        filterKey,
        defaultFilter
    )
    const popupAddManualDailyAttendance = useVisible()
    const popupBatchDailyAttendanceUpdates = useVisible()
    const [exporting, setExporting] = useState(false)

    const {studentProfileId, statsInfo} = props
    const {eventType: filterEventType, majorVersionIds, phaseIds, shiftIds, campusIds, programIds} = filter
    const [searchDebounce, setSearchDebounce] = useState(searchValue)
    const [batchStudents, setBatchStudents] = useState<any[]>([])
    const currentProfilePermissions = useCurrentProfilePermissions()

    const canAddAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const listRollCallQuery = useQuery(
        ["list-roll-call", page, pageSize, filter, searchDebounce, statsInfo, date, canAddAttendance],
        async () => {
            const params = getFilterParams()
            const {data, total} = await attendanceService.getListRollCall({
                range: {
                    page,
                    pageSize
                },
                ...params
            })

            const students = (data || []).map((item) => {
                return normalizeItem(item)
            })
            return {
                data: students,
                total
            }
        },
        {
            enabled: isLoadedTableFuncs,
            onSuccess: (data) => {
                props.dispatch({data: data.data, total: data.total})
            },
            onError: (err) => {
                handleError(err)
            },
            initialData: {
                data: [],
                total: 0
            }
        }
    )

    const listAllRollCallQuery = useQuery(
        ["list-all-roll-call", filter, searchDebounce, statsInfo, date],
        async () => {
            const params = getFilterParams()
            const {data} = await attendanceService.getListRollCall({
                range: {
                    page: 1,
                    pageSize: parseInt(total)
                },
                ...params
            })

            const students = (data || []).map((item) => {
                return normalizeItem(item)
            })
            return students
        },
        {
            enabled: isLoadedTableFuncs && popupBatchDailyAttendanceUpdates.isVisible,
            onSuccess: (data) => {
                setBatchStudents(data)
            },
            onError: (err) => {
                handleError(err)
            },
            initialData: []
        }
    )

    const createDailyAttendanceQuery = useMutation(
        async (payload: {
            eventType: AttendanceEventType
            majorVersionId: number
            profileId: number
            campusId: number
            timezone: string
        }) => {
            const {eventType, majorVersionId, profileId, campusId, timezone} = payload
            const filterDate = moment(date)
            const createdAt = moment()
            createdAt.set({
                date: filterDate.date(),
                month: filterDate.month(),
                year: filterDate.year()
            })

            const params = {
                majorVersionId,
                eventType: eventType,
                profileId,
                campusId,
                message: "Manual create daily attendance",
                createdAt: moment(createdAt).tz(timezone, true).toISOString()
            }
            await attendanceService.createDailyAttendance(params)
        },
        {
            onSuccess() {
                listRollCallQuery.refetch()
            },
            onError: (err) => {
                handleError(err)
            }
        }
    )

    useEffect(() => {
        props.dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: listRollCallQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    const getFilterParams = () => {
        const currentMajorVersionIds = statsInfo.map((info) => info.id)
        const currentPhaseIds = statsInfo.map((info) => info.phaseId).filter(Boolean)
        const currentShiftIds = statsInfo.map((info) => info.shiftId).filter(Boolean)
        const currentProgramIds = statsInfo.map((info) => info.programId).filter(Boolean)
        const currentCampusIds = statsInfo.map((info) => info.campusId).filter(Boolean)
        const _majorVersionIds =
            majorVersionIds?.filter((majorVersionId) => currentMajorVersionIds.includes(majorVersionId)) ?? []
        const _phaseIds = phaseIds?.filter((phaseId) => currentPhaseIds.includes(phaseId)) ?? []
        const _shiftIds = shiftIds?.filter((shiftId) => currentShiftIds.includes(shiftId)) ?? []
        const _campusIds = campusIds?.filter((campusId) => currentCampusIds.includes(campusId)) ?? []
        const _programIds = programIds?.filter((programId) => currentProgramIds.includes(programId)) ?? []
        const params: any = {
            campusIds: !isEmpty(currentFilter.campuses)
                ? currentFilter.campuses.map((campus) => campus.id).concat(_campusIds)
                : campusIds,
            instructorProfileIds: !isEmpty(currentFilter.instructors)
                ? currentFilter.instructors.map((instructor) => instructor.profileId)
                : undefined,
            majorVersionIds: !isEmpty(currentFilter.programVersions)
                ? currentFilter.programVersions.map((pv) => pv.id).concat(_majorVersionIds)
                : majorVersionIds,
            programIds: !isEmpty(currentFilter.programs)
                ? currentFilter.programs.map((p) => p.id).concat(_programIds)
                : programIds,
            eventTypes: undefined,
            phaseIds: !isEmpty(currentFilter.phases)
                ? currentFilter.phases.map((phase) => phase.phaseId).concat(_phaseIds)
                : _phaseIds,
            shiftIds: !isEmpty(currentFilter.shifts)
                ? currentFilter.shifts.map((shift) => shift.shiftId).concat(_shiftIds)
                : _shiftIds,
            startingTermIds: !isEmpty(currentFilter.startingTerms)
                ? currentFilter.startingTerms.map((term) => term.id)
                : undefined,
            statusIds: !isEmpty(currentFilter.studentStatuses)
                ? currentFilter.studentStatuses.map((status) => status.statusId)
                : undefined,
            groupIds: !isEmpty(currentFilter.groups) ? currentFilter.groups.map((group) => group.id) : undefined,
            hasCheckin: currentFilter.filterCheckin === DailyAttendanceFilterCheckin.HasCheckin,
            hasCheckout: currentFilter.filterCheckout === DailyAttendanceFilterCheckout.HasCheckout,
            isMissingCheckout: currentFilter.filterCheckout === DailyAttendanceFilterCheckout.MissingCheckout,
            isMissingCheckin: currentFilter.filterCheckin === DailyAttendanceFilterCheckin.MissingCheckin,
            isOverlappingTime: !!currentFilter.overlappingTime,
            isLateCheckin: currentFilter.filterCheckin === DailyAttendanceFilterCheckin.LateCheckin
        }

        if (date) {
            const filterDate = moment(date)
            params.date = filterDate.format("YYYY-MM-DD")
        } else {
            params.date = moment().format("YYYY-MM-DD")
        }

        if (searchDebounce?.length) {
            params.search = searchDebounce
        }

        if (
            (_majorVersionIds?.length ||
                _phaseIds?.length ||
                _shiftIds?.length ||
                _programIds?.length ||
                _campusIds?.length) &&
            filterEventType
        ) {
            if (filterEventType === FilterEventType.Active) {
                params.eventTypes = []
            } else if (filterEventType === FilterEventType.Break) {
                params.eventTypes = [AttendanceEventType.BreakStart]
            } else if (filterEventType === FilterEventType.Checkout) {
                params.eventTypes = [AttendanceEventType.Checkout]
            } else if (filterEventType === FilterEventType.InSchool) {
                params.eventTypes = [AttendanceEventType.Checkin, AttendanceEventType.BreakEnd]
            } else if (filterEventType === FilterEventType.Missed) {
                params.isMissed = true
            }
        }

        const profileId = model.profileId

        if (!model.isStaffOrAdmin() && profileId) {
            params.studentProfileIds = [profileId]
        } else if (studentProfileId) {
            params.studentProfileIds = [parseInt(studentProfileId)]
        }
        return params
    }

    const renderIconAttendance = (eventType: string) => {
        if (eventType === "check-out") {
            return <Icon icon="CHECKOUT_FILL" color={"#1E90FF"} className={styles.checkOutIcon} />
        } else if (eventType === "check-in") {
            return <Icon icon="CHECKIN_FILL" color={"#1E90FF"} className={styles.rightIcon} />
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else {
            return null
        }
    }

    const onPressInOut = (
        e: React.MouseEvent,
        payload: {
            eventType: AttendanceEventType
            majorVersionId: number
            profileId: number
            campusId: number
            timezone: string
        }
    ) => {
        e.stopPropagation()

        createDailyAttendanceQuery.mutate(payload)
    }

    const renderButtonInOut = (payload: {
        majorVersionId: number
        profileId: number
        campusId: number
        typeButton: AttendanceEventType
        enabled: boolean
        timezone: string
    }) => {
        const {majorVersionId, profileId, campusId, typeButton, enabled, timezone} = payload
        return (
            <div
                onClick={(e) =>
                    enabled && onPressInOut(e, {eventType: typeButton, majorVersionId, profileId, campusId, timezone})
                }
                style={{
                    opacity: enabled ? 1 : 0.3
                }}
                className={rollCallStyles.buttonOut}>
                {[AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(typeButton) ? (
                    <div className={rollCallStyles.actionButton} style={{backgroundColor: "#18A957"}}>
                        <Icon icon="IN_CLASS" color="white" />
                    </div>
                ) : (
                    <div className={rollCallStyles.actionButton} style={{backgroundColor: "#DF1642"}}>
                        <Icon icon="OUT_CLASS" color="white" />
                    </div>
                )}
            </div>
        )
    }

    const customArrowFunction = ({isActive}: any) => (
        <span className={rollCallStyles.studentNameIcon}>
            <Icon icon={isActive ? "ARROW_DOWN" : "ARROW_RIGHT"} className={rollCallStyles.arrowIcon} />
        </span>
    )

    const renderStudentNameHtml = (payload: {
        studentName: string
        timezone: string
        attendances: Array<{attendanceId: number; events: AttendanceEvent[]; attendedHours: number}>
    }) => {
        const {studentName, attendances} = payload

        if (!attendances?.length) {
            return <div className={rollCallStyles.studentName}>{studentName}</div>
        }

        return (
            <Collapse expandIcon={customArrowFunction} defaultActiveKey={"1"}>
                <Panel
                    className={rollCallStyles.studentName}
                    header={<div className={rollCallStyles.studentName}>{studentName}</div>}
                    key="1">
                    <div className={rollCallStyles.studentNameHtml}>
                        {attendances.map((item, index) => {
                            return (
                                <div key={item.attendanceId} className={rollCallStyles.attendanceEvents}>
                                    <div className={rollCallStyles.eventsContainer}>
                                        {item.events?.map((event, index) => {
                                            return (
                                                <>
                                                    <div className={rollCallStyles.event}>
                                                        {event.eventType === AttendanceEventType.Checkin ? (
                                                            <Icon
                                                                icon="CHECKIN_FILL"
                                                                className={rollCallStyles.eventIcon}
                                                                color={"#18a957"}
                                                            />
                                                        ) : null}
                                                        {event.eventType === AttendanceEventType.Checkout ? (
                                                            <Icon
                                                                icon="CHECKOUT_FILL"
                                                                className={rollCallStyles.eventIcon}
                                                                color={"#DF1642"}
                                                            />
                                                        ) : null}
                                                        {event.eventType === AttendanceEventType.BreakEnd ? (
                                                            <Icon
                                                                icon="ATTENDANCE_IN"
                                                                className={rollCallStyles.eventIcon}
                                                                color={"#18a957"}
                                                            />
                                                        ) : null}
                                                        {event.eventType === AttendanceEventType.BreakStart ? (
                                                            <Icon
                                                                icon="ATTENDANCE_OUT"
                                                                className={rollCallStyles.eventIcon}
                                                                color={"#DF1642"}
                                                            />
                                                        ) : null}
                                                        <span className={rollCallStyles.eventTime}>
                                                            {moment(event.createdAt)
                                                                .tz(payload.timezone)
                                                                .format(model.getUserTimeFormat())}
                                                        </span>
                                                    </div>
                                                    {index !== item.events?.length - 1 ? (
                                                        <div className={rollCallStyles.eventSeparator} />
                                                    ) : null}
                                                </>
                                            )
                                        })}
                                    </div>
                                    <span>ATTD: {roundTwoDigits((item.attendedHours ?? 0) * 60)}</span>
                                </div>
                            )
                        })}
                    </div>
                </Panel>
            </Collapse>
        )
    }

    const normalizeItem = (daily, isExport: boolean = false) => {
        const student = daily?.student ?? {}
        const latestEventType = daily.latestEventType
        const shifts: any[] = uniqBy(student.shifts ?? [], "shiftId")
        const phases: any[] = uniqBy(student.phases ?? [], "phaseId")

        let typeButton: AttendanceEventType = AttendanceEventType.Checkin
        if ([AttendanceEventType.BreakStart, AttendanceEventType.Checkout].includes(latestEventType)) {
            typeButton = AttendanceEventType.Checkin
        } else if ([AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(latestEventType)) {
            typeButton = AttendanceEventType.Checkout
        }

        let result = {
            ...daily,
            phaseNames: phases.map((phase) => phase.name).join(",") ?? "",
            shiftNames: shifts.map((shift) => shift.name).join(",") ?? "",
            id: student.id,
            studentStatusHtml: student.status?.name ?? "",
            statusHtml: isExport ? latestEventType : renderIconAttendance(latestEventType),
            checkHtml: renderButtonInOut({
                majorVersionId: student.currentMajorVersionId?.id,
                profileId: student.id,
                campusId: get(student, ["campuses", 0, "id"]),
                typeButton: typeButton,
                enabled: canAddAttendance && daily.currentDateByStudentTimezone === moment(date).format("YYYY-MM-DD"),
                timezone: student.timezone ?? moment.tz.guess()
            }),
            customProfileId: student?.customProfileId ?? "",
            studentName: getFullName(student),
            studentNameHtml: renderStudentNameHtml({
                studentName: getFullName(student),
                timezone: student.timezone,
                attendances: daily.attendances ?? []
            }),
            attendedMinutes: roundTwoDigits((daily.attendedHours ?? 0) * 60),
            checkinHtml: daily.checkinCreatedAt
                ? moment(daily.checkinCreatedAt).tz(student.timezone).format(model.getUserTimeFormat())
                : "",
            checkoutHtml: daily.checkoutCreatedAt
                ? moment(daily.checkoutCreatedAt).tz(student.timezone).format(model.getUserTimeFormat())
                : "",
            accommodatedHoursHtml: isExport ? (
                student?.isAccommodatedHoursEnabled ? (
                    YesNo.Yes
                ) : (
                    YesNo.No
                )
            ) : (
                <Checkbox checked={student.isAccommodatedHoursEnabled} disabled />
            ),
            majorVersionHtml: student.currentMajorVersionId?.versionName
                ? `${student.currentMajorVersionId.versionName} (${student.currentMajorVersionId.versionCode})`
                : "",
            majorHtml: student.currentMajor?.name ? `${student.currentMajor.name} (${student.currentMajor.code})` : ""
        }

        return result
    }

    function getColumns() {
        return [
            {
                title: "Student Id",
                field: "customProfileId",
                headerStyle: {width: "100px"}
            },

            {
                title: "Student Name",
                field: "studentNameHtml",
                headerStyle: {minWidth: "300px"}
            },
            {
                title: "Check",
                field: "checkHtml",
                headerStyle: {width: "80px"}
            },
            {
                title: "Student Status",
                field: "studentStatusHtml",
                headerStyle: {width: "90px"}
            },
            {
                title: "Accommodated Hours",
                field: "accommodatedHoursHtml",
                headerStyle: {width: "90px"}
            },
            {
                title: "Phase",
                field: "phaseNames",
                headerStyle: {width: "120px"}
            },
            {
                title: "Shift",
                field: "shiftNames",
                headerStyle: {width: "120px"}
            },
            {
                title: "Major",
                field: "majorHtml",
                headerStyle: {width: "150px"}
            },
            {
                title: "Major Version",
                field: "majorVersionHtml",
                headerStyle: {width: "150px"}
            },
            {
                title: "Status",
                field: "statusHtml",
                headerStyle: {width: "80px"}
            },
            {
                title: "Checkin",
                field: "checkinHtml",
                headerStyle: {width: "90px"}
            },
            {
                title: "Checkout",
                field: "checkoutHtml",
                headerStyle: {width: "90px"}
            },
            {
                title: "Attended Minutes",
                field: "attendedMinutes",
                headerStyle: {width: "90px"}
            }
        ]
    }

    function getFields() {
        return [
            "Student Id",
            "Student Name",
            "Student Status",
            "Accommodated Hours",
            "Phase",
            "Shift",
            "Major",
            "Major Version",
            "Status",
            "Checkin",
            "Checkout",
            "Attended Minutes",
            "Check"
        ]
    }

    const onChangeFilter = (key, value) => {
        const newFilter = {...currentFilter}
        newFilter[key] = value
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        applyFilter()
        listRollCallQuery.refetch()
    }

    const handleExportFile = async (type: ExportFileType) => {
        setExporting(true)
        try {
            const params = getFilterParams()
            const {data} = await attendanceService.getListRollCall({
                range: {
                    page: 1,
                    pageSize: total
                },
                ...params
            })
            const students = (data || []).map((daily) => {
                return normalizeItem(daily, true)
            })
            const exportColumns = cloneDeep([
                {
                    title: "Date",
                    field: "date",
                    headerStyle: {width: "90px"}
                },
                ...columns
            ]).filter((col) => col.title !== "Check")
            const filename = `Roll_Call_${moment().format("MM_DD_YYYY")}`
            const payload = {
                filename,
                columns: exportColumns.map((col) => col.title.toUpperCase()),
                rows: students.map((item) =>
                    exportColumns.map((col) => {
                        if (col.field === "studentNameHtml") {
                            return item["studentName"]?.toString() ?? ""
                        }
                        if (col.field === "date") {
                            return moment(date).format(model.getUserDateFormat())
                        }
                        return item[col.field]?.toString() ?? ""
                    })
                )
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (err) {
            handleError(err)
        } finally {
            setExporting(false)
        }
    }

    const allUserCampusIds = useMemo(() => {
        if (!model.isStaff()) {
            return []
        }
        const userCampuses = (model.user?.profiles || [])
            .map((profile) => (profile.campuses || []).map(({id}) => id))
            .filter(Boolean)
        return userCampuses.flat()
    }, [model.user])

    const onUpdateRowData = (updatedData) => {
        const data = getCurrentData()
        const newData = cloneDeep(data).map((item) => {
            if (item.id === updatedData.id) {
                return {
                    ...item,
                    isChecked: updatedData.isChecked
                }
            }
            return item
        })
        dispatch({data: newData})
    }

    return (
        <div style={{paddingBottom: 50}}>
            <DailyAttendanceFilter
                searchValue={searchValue}
                changeSearch={changeSearch}
                onApplyFilter={onApplyFilter}
                clearFilter={clearFilter}
                currentFilter={currentFilter}
                showDailyRoster
                onOpenAddManualDailyAttendancePopup={popupAddManualDailyAttendance.open}
                exporting={exporting}
                handleExportFile={handleExportFile}
                onChangeFilter={onChangeFilter}
                setCurrentFilter={setCurrentFilter}
                date={date}
                onChangeDate={setDate}
                savedFilterOptions={{
                    enabled: true,
                    filterKey: filterKey,
                    setFilter: setCurrentFilter
                }}
                showBatchUpdates
                onOpenBatchDailyAttendanceUpdatesPopup={popupBatchDailyAttendanceUpdates.open}
            />
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={listRollCallQuery.isFetching || createDailyAttendanceQuery.isLoading}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
            <AddManualDailyAttendance
                onRefreshData={listRollCallQuery.refetch}
                onClose={() => {
                    popupAddManualDailyAttendance.close()
                    listRollCallQuery.refetch()
                }}
                isShow={popupAddManualDailyAttendance.isVisible}
            />
            <BatchDailyAttendanceUpdates
                isShow={popupBatchDailyAttendanceUpdates.isVisible}
                onClose={popupBatchDailyAttendanceUpdates.close}
                students={batchStudents}
                isFetching={listAllRollCallQuery.isFetching}
                date={date}
                onSuccess={() => {
                    popupBatchDailyAttendanceUpdates.close()
                    listRollCallQuery.refetch()
                }}
            />
        </div>
    )
}
export const RollCall = KlassappTableHOC(RollCallTable)
